import React, { useRef, useState, useEffect } from "react";
import { typography, surfaces, borders, textColors, interactive } from "../../../design-system/tokens";

const ROTATION_PRESETS = [
  { value: 0, label: "0°" },
  { value: 90, label: "90°" },
  { value: 180, label: "180°" },
  { value: 270, label: "270°" },
];

export default function ImageRotationField({ label, help, value, onChange, infoText }) {
  const normalized = value || 0;
  const dialRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);

  const calculateAngleFromEvent = (e, dialElement) => {
    const rect = dialElement.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;
    
    const clientX = e.clientX || (e.touches && e.touches[0]?.clientX);
    const clientY = e.clientY || (e.touches && e.touches[0]?.clientY);
    
    if (clientX === undefined || clientY === undefined) return null;
    
    const deltaX = clientX - centerX;
    const deltaY = clientY - centerY;
    
    // Calculate angle in degrees (0° at top, clockwise)
    let angle = Math.atan2(deltaY, deltaX) * (180 / Math.PI) + 90;
    if (angle < 0) angle += 360;
    
    return Math.round(angle);
  };

  const handleMouseDown = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleMouseMove = (e) => {
    if (!isDragging || !dialRef.current) return;
    
    const angle = calculateAngleFromEvent(e, dialRef.current);
    if (angle !== null) {
      onChange(angle);
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      window.addEventListener('touchmove', handleMouseMove);
      window.addEventListener('touchend', handleMouseUp);
      
      return () => {
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
        window.removeEventListener('touchmove', handleMouseMove);
        window.removeEventListener('touchend', handleMouseUp);
      };
    }
  }, [isDragging, normalized]);

  // Calculate position of small ball on the edge
  const radius = 60; // radius of the big circle
  const handleRadius = 8; // radius of the small ball
  const angleInRadians = ((normalized - 90) * Math.PI) / 180; // Adjust so 0° is at top
  const handleX = radius + radius * Math.cos(angleInRadians) - handleRadius;
  const handleY = radius + radius * Math.sin(angleInRadians) - handleRadius;

  return (
    <div className="field-wrapper">
      {label && (
        <label className={`block mb-2 ${typography.label} ${textColors.secondary}`}>
          {label}
        </label>
      )}

      {/* Circular dial control */}
      <div className="flex justify-center mb-4">
        <div 
          ref={dialRef}
          className="relative cursor-pointer select-none"
          style={{ width: radius * 2, height: radius * 2 }}
          onMouseDown={handleMouseDown}
          onTouchStart={handleMouseDown}
        >
          {/* Angle indicator line (rendered first so it's behind everything) */}
          <div
            className="absolute bg-blue-500 dark:bg-blue-400 opacity-30"
            style={{
              width: 2,
              height: radius - 10,
              left: radius - 1,
              top: 10,
              transformOrigin: `1px ${radius - 10}px`,
              transform: `rotate(${normalized}deg)`,
              transition: isDragging ? 'none' : 'transform 0.2s ease',
            }}
          />

          {/* Big ball (outer circle) */}
          <div 
            className={`absolute inset-0 rounded-full border-4 ${borders.default} ${surfaces.secondary} flex items-center justify-center`}
            style={{ 
              boxShadow: isDragging 
                ? '0 0 0 4px rgba(59, 130, 246, 0.2)' 
                : '0 2px 8px rgba(0, 0, 0, 0.1)'
            }}
          >
            {/* Image placeholder that rotates */}
            <div
              style={{
                transform: `rotate(${normalized}deg)`,
                transition: isDragging ? 'none' : 'transform 0.2s ease',
              }}
            >
              <svg 
                className={`w-8 h-8 ${textColors.tertiary}`}
                fill="currentColor" 
                viewBox="0 0 20 20"
              >
                <path fillRule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clipRule="evenodd" />
              </svg>
            </div>
          </div>

          {/* Small ball on the edge (handle) */}
          <div
            className="absolute rounded-full bg-blue-500 dark:bg-blue-400 shadow-lg"
            style={{
              width: handleRadius * 2,
              height: handleRadius * 2,
              left: handleX,
              top: handleY,
              transition: isDragging ? 'none' : 'left 0.2s ease, top 0.2s ease',
              border: '2px solid white',
              boxShadow: '0 2px 6px rgba(0, 0, 0, 0.3)',
            }}
          />
        </div>
      </div>

      {/* Current angle display */}
      <div className={`text-center mb-3 ${typography.h5} ${textColors.primary}`}>
        {normalized}°
      </div>

      {/* Preset shortcuts (numbers only) */}
      <div className="grid grid-cols-4 gap-2">
        {ROTATION_PRESETS.map((preset) => {
          const selected = normalized === preset.value;
          
          return (
            <button
              key={preset.value}
              type="button"
              onClick={() => onChange(preset.value)}
              className={`py-2 px-3 border rounded-lg transition-all ${typography.button} ${
                selected 
                  ? `${interactive.selectedBg} text-white ${interactive.selectedBorder} shadow-sm` 
                  : `${borders.default} ${surfaces.tertiary} ${surfaces.hover} ${textColors.primary}`
              }`}
              title={`Set rotation to ${preset.label}`}
            >
              {preset.label}
            </button>
          );
        })}
      </div>

      {help && (
        <div className={`mt-2 ${typography.caption} ${textColors.tertiary}`}>
          {help}
        </div>
      )}
    </div>
  );
}

